'use client';

import { cn } from '@/lib/utils';
import { formatCurrency, KRW, type Currency } from '../../types';

interface CurrencyDisplayProps {
  amount: number;
  currency?: Currency;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showSign?: boolean;
}

export function CurrencyDisplay({
  amount,
  currency = KRW,
  className,
  size = 'md',
  showSign = false,
}: CurrencyDisplayProps) {
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg font-semibold',
  };

  const sign = showSign && amount > 0 ? '+' : '';
  const colorClass = showSign
    ? amount > 0
      ? 'text-green-600 dark:text-green-400'
      : amount < 0
        ? 'text-red-600 dark:text-red-400'
        : ''
    : '';

  return (
    <span className={cn(sizeClasses[size], colorClass, className)}>
      {sign}
      {formatCurrency(amount, currency)}
    </span>
  );
}

// 금액 입력 컴포넌트
interface CurrencyInputProps {
  value: number;
  onChange: (value: number) => void;
  currency?: Currency;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
}

export function CurrencyInput({
  value,
  onChange,
  currency = KRW,
  placeholder = '0',
  className,
  disabled = false,
  min,
  max,
}: CurrencyInputProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^0-9-]/g, '');
    let numValue = parseInt(rawValue, 10) || 0;

    if (min !== undefined && numValue < min) numValue = min;
    if (max !== undefined && numValue > max) numValue = max;

    onChange(numValue);
  };

  return (
    <div className={cn('relative', className)}>
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
        {currency.symbol}
      </span>
      <input
        type="text"
        value={value.toLocaleString('ko-KR')}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          'w-full h-10 pl-8 pr-4 rounded-lg border border-border bg-background',
          'text-sm text-right',
          'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'transition-colors'
        )}
      />
    </div>
  );
}
